import React, { useState } from 'react';
import styled from 'styled-components';

import BranchStaticField from '../components/BranchStaticField';
import EditableNumberField from '../components/EditableNumberField';
import Switch from '../../Shared/Switch';
import CustomMaterialTooltip from '../../Shared/StyledTooltip';
import { formatDate, withFund } from '../../../utils/formatters';
import { createToggleModalOpen } from '../utils';
import NumberPromptModal from '../components/NumberPromptModal';
import { SettingHeader, SettingsContainer } from '../components/BranchComponents';


function isPaidDeliveryEnabled(pharmacy) {
  if (!pharmacy.charge_for_delivery) return false;

  for (const deliveryOption of Object.keys(pharmacy.charge_for_delivery)) {
    for (const orderType of Object.keys(pharmacy.charge_for_delivery[deliveryOption])) {
      if (pharmacy.charge_for_delivery[deliveryOption][orderType]) return true;
    }
  }

  return false;
}

const PaidDeliverySettings = ({ pharmacy, organisation, onChange }) => {
  const paidDeliveryEnabled = isPaidDeliveryEnabled(pharmacy);
  const { paid_delivery: pharmacyPaidDelivery } = pharmacy;
  const { timeslot_limit, free_paid_delivery_age, go_live_date, pharmacy_select_timeslot } = pharmacyPaidDelivery || {};
  const { paid_delivery: organisationPaidDelivery } = organisation || {};
  const { delivery_cost, delivery_fee, concession_cost, concession_fee } =
    organisationPaidDelivery || {};

  const [isFreeDeliveryAgeModalOpen, setIsFreeDeliveryAgeModalOpen] = useState(false);

  const freeDeliveryAgeModalSubmitHandler = freePaidDeliveryAge => {
    onChange('paid_delivery.free_paid_delivery_age', freePaidDeliveryAge);
    setIsFreeDeliveryAgeModalOpen(false);
  };

  const ageExemptionSwitchHandler = (_, checked) => {
    if (!checked) return onChange('paid_delivery.free_paid_delivery_age', null);

    setIsFreeDeliveryAgeModalOpen(true);
  };

  const createFieldChangeHandler = field => value => {
    onChange(field, value);
  };

  return (
    <>
      {paidDeliveryEnabled &&
      <SettingsContainer>
        <SettingHeader>Paid delivery settings</SettingHeader>
        <OptionsContainer>

          <BranchStaticField label="Go live date" value={formatDate(go_live_date)} />
          <EditableNumberField
            label="Max deliveries/slot"
            question="How many deliveries pharmacy can do in a single time slot?"
            value={timeslot_limit}
            onChange={createFieldChangeHandler('paid_delivery.timeslot_limit')}
          />
          <Switch
            label="Auto age exemption"
            onChange={ageExemptionSwitchHandler}
            checked={!!free_paid_delivery_age}
          />
          {free_paid_delivery_age && (
            <EditableNumberField
              label="Free paid delivery age"
              question="After what age do you want patient to be exempt from delivery payment?"
              value={free_paid_delivery_age}
              onChange={createFieldChangeHandler('paid_delivery.free_paid_delivery_age')}
            />
          )}
          <BranchStaticField label="Delivery charge" value={withFund(delivery_cost || 0)} />
          <BranchStaticField label="Delivery service charge" value={withFund(delivery_fee || 0)} />
          <BranchStaticField label="Concession delivery charge" value={withFund(concession_cost || 0)} />
          <BranchStaticField label="Concession service charge" value={withFund(concession_fee || 0)} />
          <CustomMaterialTooltip title="When enabled, pharmacy will determine delivery timeslots">
            <Switch
              label="Pharmacy manages delivery timeslots"
              checked={Boolean(pharmacy_select_timeslot)}
              onChange={(_, checked) => onChange('paid_delivery.pharmacy_select_timeslot', checked)}
            />
          </CustomMaterialTooltip>
        </OptionsContainer>
        <NumberPromptModal
          open={isFreeDeliveryAgeModalOpen}
          onSubmit={freeDeliveryAgeModalSubmitHandler}
          onCancel={createToggleModalOpen(setIsFreeDeliveryAgeModalOpen)}
          fieldText="Free paid delivery age"
          title="After what age do you want patient to be exempt from delivery payment?"
        />
      </SettingsContainer>
      }
    </>
  );
};

export default PaidDeliverySettings;

const OptionsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;

  & > * {
    height: 36px;
  }
`;
